import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import ChestIcon from '../components/ChestIcon';
import type { ChallengeUpdateItem, LevelInfo } from '../hooks/useChallengeUpdates';
import GemIcon from '@/components/icons/GemIcon';

const toPersianDigits = (s: string) => s.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
const toPersianNumber = (n: number | string) => toPersianDigits(String(n));

interface ChallengesPageState {
  challenges?: ChallengeUpdateItem[];
  level_info?: LevelInfo | null;
  book_id?: number | null;
  initialGems?: number;
}

const ChallengesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Get data from location state (passed from GoalsRewardPage or AchievementPage)
  const locationState = (location.state || {}) as ChallengesPageState;
  const challenges = locationState.challenges || [];
  const level_info = locationState.level_info || null;
  const initialGems = locationState.initialGems || 0;
  const book_id = locationState.book_id || null;

  // Get bookId from response, location state, or localStorage
  const lastLearningPathBookId = localStorage.getItem('lastLearningPathBookId');
  const bookIdFromStorage = lastLearningPathBookId ? parseInt(lastLearningPathBookId, 10) : null;
  const bookId = book_id || (bookIdFromStorage && bookIdFromStorage > 0 ? bookIdFromStorage : null);

  // Animation states
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [levelUpAnimating, setLevelUpAnimating] = useState(false);
  const [chestAnimating, setChestAnimating] = useState<boolean[]>([]);

  // Display states for level bar
  const [displayedGems, setDisplayedGems] = useState(0);
  const [displayedLevel, setDisplayedLevel] = useState(1);
  const [displayedLevelXp, setDisplayedLevelXp] = useState(0);
  const [displayedNextLevelXp, setDisplayedNextLevelXp] = useState(150);
  const [levelProgress, setLevelProgress] = useState(0);

  // Progress widths for each challenge (percentage)
  const [widths, setWidths] = useState<number[]>([]);
  const [widthsInitialized, setWidthsInitialized] = useState(false);

  // Refs for tracking current values
  const currentXpRef = useRef(0);
  const currentLevelRef = useRef(1);
  const currentNextLevelXpRef = useRef(150);
  const currentGemsRef = useRef(0);
  const animationStartedRef = useRef(false);

  // Initialize display states when level_info is available
  useEffect(() => {
    if (level_info) {
      setDisplayedLevel(level_info.previous_level);
      setDisplayedLevelXp(level_info.previous_level_xp);
      setDisplayedNextLevelXp(level_info.previous_next_level_xp);
      setLevelProgress(
        level_info.previous_next_level_xp > 0
          ? (level_info.previous_level_xp / level_info.previous_next_level_xp) * 100
          : 0
      );
      currentXpRef.current = level_info.previous_level_xp;
      currentLevelRef.current = level_info.previous_level;
      currentNextLevelXpRef.current = level_info.previous_next_level_xp;
    }
  }, [level_info]);

  // Initialize gems from auth/me
  useEffect(() => {
    if (initialGems > 0) {
      setDisplayedGems(initialGems);
      currentGemsRef.current = initialGems;
    }
  }, [initialGems]);

  // Initialize widths based on previousProgress
  useEffect(() => {
    if (challenges.length > 0 && !widthsInitialized) {
      const initialWidths = challenges.map((c) =>
        c.targetValue > 0 ? Math.min((c.previousProgress / c.targetValue) * 100, 100) : 0
      );
      setWidths(initialWidths);
      setChestAnimating(new Array(challenges.length).fill(false));
      setWidthsInitialized(true);
    }
  }, [challenges, widthsInitialized]);

  // Helper: animate gems
  const animateGems = (amount: number): Promise<void> => {
    return new Promise((resolve) => {
      if (!amount || amount <= 0) {
        resolve();
        return;
      }
      const startGems = currentGemsRef.current;
      const endGems = startGems + amount;
      const steps = 20;
      const increment = amount / steps;
      let current = startGems;
      let step = 0;

      const interval = setInterval(() => {
        step++;
        current += increment;
        if (step >= steps) {
          currentGemsRef.current = endGems;
          setDisplayedGems(endGems);
          clearInterval(interval);
          resolve();
        } else {
          setDisplayedGems(Math.floor(current));
        }
      }, 20);
    });
  };

  // Helper: animate XP bar with smooth transition (handles level up if needed)
  const animateXp = async (amount: number): Promise<void> => {
    if (!amount || amount <= 0 || !level_info) return;

    let remainingXp = amount;

    while (remainingXp > 0) {
      const currentXp = currentXpRef.current;
      const maxXp = currentNextLevelXpRef.current;
      const xpToFill = maxXp - currentXp;

      if (remainingXp >= xpToFill && currentLevelRef.current < level_info.level) {
        remainingXp -= xpToFill;
        // Animate to 100%
        await animateLevelProgress(currentXp, maxXp, maxXp);
        await new Promise((r) => setTimeout(r, 20));

        setLevelUpAnimating(true);
        await new Promise((r) => setTimeout(r, 150));

        currentLevelRef.current = level_info.level;
        currentXpRef.current = 0;
        currentNextLevelXpRef.current = level_info.next_level_xp;

        setDisplayedLevel(level_info.level);
        setDisplayedNextLevelXp(level_info.next_level_xp);
        setDisplayedLevelXp(0);
        setLevelProgress(0);

        await new Promise((r) => setTimeout(r, 20));
        setLevelUpAnimating(false);
        await new Promise((r) => setTimeout(r, 20));

        const totalXpAfterLevelUp = (level_info.received_xp || 0) + remainingXp;
        if (totalXpAfterLevelUp > 0) {
          await animateLevelProgress(0, totalXpAfterLevelUp, level_info.next_level_xp);
        }

        if (level_info.received_gems && level_info.received_gems > 0) {
          await animateGems(level_info.received_gems);
          await new Promise((r) => setTimeout(r, 20));
        }

        remainingXp = 0;
      } else {
        const newXp = currentXp + remainingXp;
        await animateLevelProgress(currentXp, newXp, currentNextLevelXpRef.current);
        remainingXp = 0;
      }
    }
  };

  // Helper: animate level progress bar smoothly
  const animateLevelProgress = (startXp: number, endXp: number, maxXp: number): Promise<void> => {
    return new Promise((resolve) => {
      const durationMs = 700;
      const start = performance.now();

      const step = (now: number) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / durationMs);
        const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        const currentXp = startXp + (endXp - startXp) * eased;
        const progress = maxXp > 0 ? (currentXp / maxXp) * 100 : 0;

        setDisplayedLevelXp(Math.round(currentXp));
        setLevelProgress(Math.min(progress, 100));

        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          currentXpRef.current = endXp;
          resolve();
        }
      };

      requestAnimationFrame(step);
    });
  };

  // Animate challenge progress bar (slower animation)
  const animateChallengeProgress = (idx: number, targetPercent: number): Promise<void> => {
    return new Promise((resolve) => {
      const startPercent = widths[idx] || 0;
      // Cap target at 100%
      const cappedTarget = Math.min(targetPercent, 100);
      const durationMs = 800;
      const start = performance.now();

      const step = (now: number) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / durationMs);
        const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        const current = startPercent + (cappedTarget - startPercent) * eased;

        setWidths((prev) => {
          const copy = [...prev];
          copy[idx] = Math.min(current, 100);
          return copy;
        });

        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(step);
    });
  };


  // Main animation sequence
  useEffect(() => {
    if (animationStartedRef.current) return;
    if (challenges.length === 0) return;
    // Wait for widths to be initialized before starting animation
    if (!widthsInitialized || widths.length === 0) return;

    animationStartedRef.current = true;

    const runSequence = async () => {
      for (let idx = 0; idx < challenges.length; idx++) {
        const challenge = challenges[idx];
        const targetPercent =
          challenge.targetValue > 0 ? Math.min((challenge.currentProgress / challenge.targetValue) * 100, 100) : 0;

        // Only animate if progress changed (not already completed before)
        const hasProgressChanged = challenge.previousProgress !== challenge.currentProgress;
        // Check if challenge was already completed before this session
        const wasAlreadyCompleted = challenge.previousProgress >= challenge.targetValue;

        if (hasProgressChanged) {
          // Animate progress bar from previousProgress to currentProgress
          await animateChallengeProgress(idx, targetPercent);
        }

        // If challenge completed NOW (not before), animate chest and add rewards
        if (challenge.status === 'completed' && hasProgressChanged && !wasAlreadyCompleted) {
          // Chest animation
          setChestAnimating((prev) => {
            const copy = [...prev];
            copy[idx] = true;
            return copy;
          });
          await new Promise((r) => setTimeout(r, 150));
          setChestAnimating((prev) => {
            const copy = [...prev];
            copy[idx] = false;
            return copy;
          });
          await new Promise((r) => setTimeout(r, 20));

          // Add earnedXp
          if (challenge.earnedXp > 0) {
            await animateXp(challenge.earnedXp);
          }

          // Add earnedGems
          if (challenge.earnedGems > 0) {
            await animateGems(challenge.earnedGems);
          }
        }
      }

      // Ensure final XP matches backend
      if (level_info && currentXpRef.current !== level_info.current_level_xp) {
        setDisplayedLevelXp(level_info.current_level_xp);
        const finalProgress =
          level_info.next_level_xp > 0 ? (level_info.current_level_xp / level_info.next_level_xp) * 100 : 0;
        setLevelProgress(finalProgress);
      }

      await new Promise((r) => setTimeout(r, 20));
      setShowContinueButton(true);
    };

    runSequence();
  }, [challenges, level_info, widthsInitialized, widths.length]);

  const handleContinue = () => {
    // Invalidate queries to refresh data on learning path
    if (bookId) {
      queryClient.invalidateQueries({ queryKey: ['my-sections', bookId] });
      queryClient.invalidateQueries({ queryKey: ['book-detail', bookId] });
    }
    queryClient.invalidateQueries({ queryKey: ['user-library'] });
    queryClient.invalidateQueries({ queryKey: ['quests'] });

    if (bookId) {
      navigate(`/learning/path`, { state: { bookId, showReferralInvite: true }, replace: true });
    } else {
      // Fallback to dashboard if no bookId available
      navigate('/dashboard', { replace: true });
    }
  };

  // If no challenges data passed, redirect to learning path
  useEffect(() => {
    if (challenges.length === 0) {
      // Invalidate queries before navigating
      if (bookId) {
        queryClient.invalidateQueries({ queryKey: ['my-sections', bookId] });
        queryClient.invalidateQueries({ queryKey: ['book-detail', bookId] });
      }
      queryClient.invalidateQueries({ queryKey: ['user-library'] });
      queryClient.invalidateQueries({ queryKey: ['quests'] });

      if (bookId) {
        navigate(`/learning/path`, { state: { bookId, showReferralInvite: true }, replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [challenges.length, bookId, navigate, queryClient]);

  // Show loading until widths are initialized
  if (challenges.length > 0 && !widthsInitialized) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#7063ff] mx-auto mb-3"></div>
          <p className="text-slate-700 font-persian text-xs">ط¯ط± ط­ط§ظ„ ط¨ط§ط±ع¯ط°ط§ط±غŒ...</p>
        </div>
      </div>
    );
  }

  // If no challenges, show loading while redirecting
  if (challenges.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#7063ff] mx-auto mb-3"></div>
          <p className="text-slate-700 font-persian text-xs">ط¯ط± ط­ط§ظ„ ط¨ط§ط±ع¯ط°ط§ط±غŒ...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-white px-4 flex flex-col"
      style={{ paddingTop: '2.25rem', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      dir="rtl"
    >
      <div className="max-w-3xl mx-auto w-full flex flex-col flex-1">
        {/* Level Bar and Gems - Fixed at top, only show if level_info exists */}
        {level_info && (
          <div className="mb-7">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex h-[30px] flex-1 items-center gap-1" style={{ direction: 'ltr' }}>
                <div className="relative h-full flex-1 overflow-visible rounded-full border-2 border-white bg-gray-200 shadow-md">
                  <div
                    className="absolute bottom-0 right-0 top-0 rounded-full transition-[width] duration-[250ms] ease-out"
                    style={{
                      width: `${levelProgress}%`,
                      backgroundColor: '#FBBF24',
                      boxShadow: '0 0 6px 1px rgba(251, 191, 36, 0.4)',
                    }}
                  />
                  <div className="relative z-10 flex h-full items-center justify-center">
                    <span dir="ltr" className="pt-0.5 text-xs font-bold leading-none text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] sm:text-sm">
                      {toPersianNumber(displayedNextLevelXp)} / {toPersianNumber(displayedLevelXp)}
                    </span>
                  </div>
                </div>
                <motion.div
                  className="relative z-20 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#F59E0B] bg-[#FBBF24] shadow-md sm:h-[42px] sm:w-[42px]"
                  animate={levelUpAnimating ? { scale: [1, 1.25, 1] } : {}}
                  transition={{ duration: 0.3 }}
                >
                  <span className="text-sm font-black text-white drop-shadow-sm sm:text-base">{toPersianNumber(displayedLevel)}</span>
                </motion.div>
              </div>

              <div className="inline-flex h-9 shrink-0 items-center gap-1 rounded-3xl bg-cyan-500 px-2 text-white shadow-md shadow-cyan-500/30 sm:h-10">
                <span className="flex size-5 items-center justify-center"><GemIcon size={18} color="white" /></span>
                <span className="relative top-[1.5px] min-w-[2ch] text-center text-[13px] font-black leading-none sm:text-sm">{toPersianNumber(displayedGems)}</span>
              </div>
            </div>
          </div>
        )}


        {/* Challenges Card - Centered in remaining space */}
        <div className="flex-1 flex flex-col justify-center -translate-y-4">
          <div
            className="relative mx-3 overflow-visible rounded-3xl px-3 py-6 ring-1 ring-black/10 bg-[#FFFDFC] sm:px-4"
            style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
          >
          <div className="space-y-2">
            {challenges.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-gray-600 text-sm font-bold font-persian">برای امروز چالشی وجود ندارد</p>
              </div>
            ) : (
              challenges.slice(0, 3).map((challenge, idx) => {
                const percent = Math.min(Math.round(widths[idx] || 0), 100);
                const rawProgress = Math.round((percent / 100) * challenge.targetValue);
                const displayProgress = Math.min(rawProgress, challenge.targetValue);
                const isCompleted = challenge.status === 'completed' && displayProgress >= challenge.targetValue;

                return (
                  <div key={challenge.challengeId}>
                    <div className="grid w-full grid-cols-[minmax(0,1fr)_52px] items-start gap-2">
                      <div className="min-w-0 w-full self-start">
                        <h2 className="mb-0 text-[13px] font-bold leading-none text-[#2d3747]">{toPersianDigits(challenge.title)}</h2>

                        {/* Progress Bar */}
                        <div
                          className="relative mt-4 box-border h-[18px] min-h-[18px] max-h-[18px] w-full shrink-0 overflow-hidden rounded-full bg-gray-200 p-0 shadow-inner"
                          style={{ height: '18px', minHeight: '18px', maxHeight: '18px' }}
                        >
                          <div
                            className="absolute bottom-0 right-0 top-0 h-[18px] min-h-[18px] max-h-[18px] rounded-full p-0"
                            style={{
                              backgroundColor: '#FBBF24',
                              width: `${percent}%`,
                              height: '18px',
                              minHeight: '18px',
                              maxHeight: '18px',
                              transition: 'none',
                              willChange: 'width',
                            }}
                          />
                          <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center">
                            <span className={`text-[10px] font-semibold leading-none ${percent >= 50 ? 'text-white' : 'text-gray-600'}`}>
                              {toPersianNumber(displayProgress)} / {toPersianNumber(challenge.targetValue)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Chest Icon with animation */}
                      <motion.div
                        animate={
                          chestAnimating[idx]
                            ? { scale: [1, 1.4, 1] }
                            : isCompleted
                            ? { scale: 1.05 }
                            : {}
                        }
                        transition={{ duration: 0.6 }}
                        className="flex h-[52px] w-[52px] shrink-0 translate-y-[2px] items-center justify-center"
                      >
                        <div className="h-11 w-11">
                          <ChestIcon />
                        </div>
                      </motion.div>
                    </div>

                    {idx < Math.min(3, challenges.length) - 1 && (
                      <div className="border-t border-gray-100 mt-3 -mx-4" />
                    )}
                  </div>
                );
              })
            )}
          </div>
          </div>

          {/* Continue Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: showContinueButton ? 1 : 0, y: showContinueButton ? 0 : 20 }}
            transition={{ duration: 0.4 }}
            onClick={handleContinue}
            disabled={!showContinueButton}
            className={`w-[82%] max-w-[340px] mx-auto py-3.5 rounded-xl text-center font-bold font-persian text-[15px] transition-all duration-300 active:scale-95 shadow-xl hover:shadow-2xl bg-[#6e61ff] text-white mt-5 flex items-center justify-center gap-3 ${
              !showContinueButton ? 'cursor-not-allowed opacity-70' : 'hover:bg-[#5a4ed9]'
            }`}
          >
            {showContinueButton ? (
              <span>پایان</span>
            ) : (
              <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
          </motion.button>
          </div>
        </div>
    </div>
  );
};

export default ChallengesPage;

